import { useEffect, useMemo, useState } from "react";
import { useDrawer } from "@hooks/useDrawer";
import { DRAWER_CONTEXT } from "@models/DrawerContext";
import { useAuth } from "@Auth/useAuth";
import { useConfirmationModalStore } from "@stores/confirmationModalStore";
import { useProductStagesStore } from "@stores/productStagesStore";
import { useStockStore } from "@stores/stockStore";
import { StockClient } from "@Clients/StockClient";
import { BackorderQueueClient } from "@Clients/BackorderQueueClient";
import { Stock } from "@models/Stock";
import { Entity } from "@models/Entity";
import { onInfoShow } from "@utils/onInfoShow";
import { onErrorShow } from "@utils/onErrorShow";
import { returnErrorMessage } from "@utils/returnErrorMessage";
import { useProducts } from "@features/products/queries/useProducts";
import { useLocations } from "@features/locations/queries/useLocations";

type StockField = "product" | "location" | "quantity" | "status";

interface StockFormState {
  product: string;
  location: string;
  quantity: number;
  status: string | null;
}

const EMPTY: StockFormState = {
  product: "",
  location: "",
  quantity: 0,
  status: null,
};

async function backorderCountFor(productId: string): Promise<number> {
  try {
    const pending =
      await BackorderQueueClient.getBackorderQueueByProductId(productId);
    return pending.length;
  } catch {
    return 0;
  }
}

/**
 * View-model for the create/edit stock drawer. Owns the field state, prefill
 * (product/location), edit population, validation, and the save path —
 * including the "one entry per product+location" merge confirm and backorder
 * processing. The component renders field cards + picker sheets against this.
 */
export function useStockForm() {
  const {
    context,
    selectedId,
    prefillProductId,
    prefillLocationId,
    close,
  } = useDrawer();
  const { setTouch } = useConfirmationModalStore();
  const { stages } = useProductStagesStore();
  const { baseUser } = useAuth();
  const { data: products = [] } = useProducts();
  const { data: locations = [] } = useLocations();
  const { getStocksByOrganizationId, stocks, createStock, updateStock } =
    useStockStore();

  const isEdit = context === DRAWER_CONTEXT.EDIT_STOCK;
  const productLocked = isEdit || !!prefillProductId;

  const [values, setValues] = useState<StockFormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<StockField, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [pendingMerge, setPendingMerge] = useState<{
    existing: Stock;
    addQty: number;
  } | null>(null);

  const set = <K extends keyof StockFormState>(
    field: K,
    value: StockFormState[K]
  ) => {
    setValues((v) => ({ ...v, [field]: value }));
    setTouch(true);
  };

  // Prefill on create (e.g. opened from a product or a location).
  useEffect(() => {
    if (context !== DRAWER_CONTEXT.CREATE_STOCK) return;
    if (prefillProductId) setValues((v) => ({ ...v, product: prefillProductId }));
    if (prefillLocationId)
      setValues((v) => ({ ...v, location: prefillLocationId }));
  }, [context, prefillProductId, prefillLocationId]);

  // Populate on edit — from the store if warm, else fetch the single row.
  useEffect(() => {
    if (!isEdit || !selectedId) return;

    const populate = (stock: Stock) =>
      setValues({
        product: stock.product_id,
        location: stock.location_id,
        quantity: stock.quantity,
        status: stock.current_status_id ?? null,
      });

    const warm = stocks.find((s) => s.id === selectedId);
    if (warm) {
      populate(warm);
      return;
    }
    let active = true;
    StockClient.getStockById(selectedId)
      .then((stock) => {
        if (active && stock) populate(stock);
      })
      .catch(() => {
        /* surfaced elsewhere; leave the form empty rather than crash */
      });
    return () => {
      active = false;
    };
  }, [isEdit, selectedId, stocks]);

  const selectedProduct = useMemo(
    () => products.find((p) => p.id === values.product) ?? null,
    [products, values.product]
  );
  const selectedLocation = useMemo(
    () => locations.find((l) => l.id === values.location) ?? null,
    [locations, values.location]
  );
  const selectedStatus = useMemo(
    () => stages.find((s) => s.id === values.status) ?? null,
    [stages, values.status]
  );

  const locationPath = (locationId: string): string => {
    const loc = locations.find((l) => l.id === locationId);
    return loc?.path?.length
      ? loc.path.map((p) => p.name).join(" / ")
      : loc?.name ?? "";
  };

  const validate = (): boolean => {
    const next: Partial<Record<StockField, string>> = {};
    if (!values.product) next.product = "Product is required";
    if (!values.location) next.location = "Location is required";
    if (!values.quantity) next.quantity = "Quantity is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const afterSave = (productId: string, count: number, verb: string) => {
    close();
    onInfoShow(
      count > 0
        ? `${verb} ${count} backorders are being processed.`
        : verb
    );
    window.dispatchEvent(
      new CustomEvent("stockUpdated", {
        detail: { productId, backorderCount: count },
      })
    );
    getStocksByOrganizationId(baseUser!.organization_id!);
  };

  const submit = async () => {
    if (!validate()) return;
    const { product, location, quantity, status } = values;
    setSubmitting(true);
    try {
      if (isEdit && selectedId) {
        const count = await backorderCountFor(product);
        await updateStock({
          id: selectedId,
          product_id: product,
          location_id: location,
          quantity,
          organization_id: baseUser!.organization_id!,
          current_status_id: status || null,
          committed_quantity: 0,
        });
        afterSave(product, count, "Stock updated successfully!");
        return;
      }

      // One entry per product+location — confirm a merge instead of duplicating.
      const existing = await StockClient.getStockByProductAndLocation(
        product,
        location
      );
      if (existing) {
        setPendingMerge({ existing, addQty: Number(quantity) });
        return;
      }

      const count = await backorderCountFor(product);
      await createStock({
        product_id: product,
        location_id: location,
        quantity,
        organization_id: baseUser!.organization_id!,
        current_status_id: status || null,
        committed_quantity: 0,
      });
      afterSave(product, count, "Stock added successfully!");
    } catch (error) {
      const message =
        error instanceof Error
          ? returnErrorMessage(Entity.STOCK, error.message)
          : null;
      onErrorShow(message || "Failed to save stock");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmMerge = async () => {
    if (!pendingMerge) return;
    const { existing, addQty } = pendingMerge;
    setSubmitting(true);
    try {
      const count = await backorderCountFor(existing.product_id);
      await updateStock({
        id: existing.id,
        product_id: existing.product_id,
        location_id: existing.location_id,
        organization_id: existing.organization_id,
        quantity: existing.quantity + addQty,
        committed_quantity: existing.committed_quantity,
        current_status_id: existing.current_status_id,
      });
      setPendingMerge(null);
      afterSave(existing.product_id, count, "Combined into the existing entry.");
    } catch {
      onErrorShow("Failed to combine stock");
    } finally {
      setSubmitting(false);
    }
  };

  return {
    isEdit,
    productLocked,
    values,
    errors,
    submitting,
    set,
    // option sources + resolved selections for the picker fields
    products,
    locations,
    stages,
    selectedProduct,
    selectedLocation,
    selectedStatus,
    locationPath,
    // save + merge
    submit,
    pendingMerge,
    confirmMerge,
    cancelMerge: () => setPendingMerge(null),
  };
}
